// WebSocket hacia la sala en tiempo real de una presentación (/api/presenter/live), con
// reconexión y latido. Lo usan la ventana del deck (rol "audience") y la vista de
// presentador en otro dispositivo (rol "presenter"). Ver docs/vista-presentador.md.

import type { DeckSyncMessage } from "@/hooks/useDeckSync";

const API_BASE = (import.meta.env.VITE_DECK_API_URL ?? "").replace(/\/+$/, "");
const PING_MS = 25_000;
const RETRY_MS = [500, 1000, 2000, 4000, 8000];

interface LiveSocketOptions {
  deckId: string;
  session: string;
  role: "audience" | "presenter";
  /** Enlace de presentador; obligatorio para el rol "presenter". */
  token?: string;
  onMessage: (message: DeckSyncMessage) => void;
  onOpen?: () => void;
  onStatus?: (open: boolean) => void;
}

export interface LiveSocket {
  send: (message: DeckSyncMessage) => void;
  close: () => void;
}

function liveUrl({ deckId, session, role, token }: LiveSocketOptions): string {
  const base = API_BASE || window.location.origin;
  const url = new URL(
    `/api/presenter/live/${encodeURIComponent(deckId)}/${encodeURIComponent(session)}`,
    base,
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.searchParams.set("role", role);
  if (token) url.searchParams.set("token", token);
  return url.toString();
}

export function openLiveSocket(options: LiveSocketOptions): LiveSocket {
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let pingTimer = 0;
  let retryTimer = 0;

  const connect = () => {
    if (closed) return;
    socket = new WebSocket(liveUrl(options));

    socket.onopen = () => {
      attempt = 0;
      options.onStatus?.(true);
      options.onOpen?.();
      pingTimer = window.setInterval(() => {
        if (socket?.readyState === WebSocket.OPEN) socket.send("ping");
      }, PING_MS);
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (event.data === "pong") return;
      try {
        options.onMessage(JSON.parse(event.data) as DeckSyncMessage);
      } catch {
        // mensaje malformado: se ignora
      }
    };

    socket.onclose = () => {
      window.clearInterval(pingTimer);
      options.onStatus?.(false);
      if (closed) return;
      const delay = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
      attempt += 1;
      retryTimer = window.setTimeout(connect, delay);
    };
  };

  connect();

  return {
    send: (message) => {
      if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
    },
    close: () => {
      closed = true;
      window.clearInterval(pingTimer);
      window.clearTimeout(retryTimer);
      socket?.close();
    },
  };
}
